class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onHeaderClick = this.onHeaderClick.bind(this);
    this.onHeaderKey = this.onHeaderKey.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onPromoSubmit = this.onPromoSubmit.bind(this);
    this.onPromoInput = this.onPromoInput.bind(this);
    this.onExternalAddClick = this.onExternalAddClick.bind(this);
    this.onExternalSubmit = this.onExternalSubmit.bind(this);
    this.busy = false;
    this.promoDraft = '';
    this.promoFeedback = { message: '', error: false };
    this.onClubReady = this.onClubReady.bind(this);
  }

  connectedCallback() {
    this.isPage = this.dataset.nsMode === 'page';
    this.eventSource = this.isPage ? 'ns-cart-page' : 'ns-cart-drawer';
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onPromoSubmit);
    this.addEventListener('input', this.onPromoInput);
    if (!this.isPage) {
      document.addEventListener('click', this.onHeaderClick);
      document.addEventListener('keydown', this.onHeaderKey);
      document.addEventListener('click', this.onExternalAddClick, true);
      document.addEventListener('submit', this.onExternalSubmit, true);
    }
    document.addEventListener('ns:club-ready', this.onClubReady);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (this.busy || event.source === this.eventSource || event.source === 'product-form') return;
      this.acceptDiscountCodes(event.cartData);
      return this.refresh().catch(() => this.showError(this.strings.error));
    });
    this.resizeObserver = new ResizeObserver(() => this.fitRecommendations());
    this.hydrate();
    this.setHeaderCartIconAccessibility();
    this.onClubReady();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onPromoSubmit);
    this.removeEventListener('input', this.onPromoInput);
    document.removeEventListener('click', this.onHeaderClick);
    document.removeEventListener('keydown', this.onHeaderKey);
    document.removeEventListener('click', this.onExternalAddClick, true);
    document.removeEventListener('submit', this.onExternalSubmit, true);
    document.removeEventListener('ns:club-ready', this.onClubReady);
    this.unsubscribeCart?.();
    this.resizeObserver?.disconnect();
    this.lastRemoved = null;
  }

  hydrate() {
    this.root = this.querySelector('[data-ns-cart-root]');
    this.panel = this.querySelector('.ns-cart-panel');
    try { this.strings = JSON.parse(this.querySelector('[data-ns-strings]')?.textContent || '{}'); } catch { this.strings = {}; }
    this.classList.toggle('is-empty', Number(this.root?.dataset.itemCount || 0) === 0);
    this.resizeObserver?.disconnect();
    if (this.panel) this.resizeObserver?.observe(this.panel);
    this.updateCount();
    this.fitRecommendations();
    const undo = this.querySelector('[data-ns-undo-message]');
    if (undo) undo.hidden = !this.lastRemoved;
    if (!this.discountCodes) this.discountCodes = this.normalizeDiscountCodes(this.cartSnapshot()?.discount_codes);
    this.paintPromo();
  }

  setHeaderCartIconAccessibility() {
    if (this.isPage) return;
    const link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', 'CartDrawer');
    link.setAttribute('aria-expanded', String(this.classList.contains('active')));
  }

  onHeaderClick(event) {
    const link = event.target.closest('#cart-icon-bubble');
    if (!link || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.open(link);
  }

  onHeaderKey(event) {
    if (event.target.closest('#cart-icon-bubble') && event.code === 'Space') {
      event.preventDefault();
      this.open(event.target.closest('#cart-icon-bubble'));
    }
  }

  getProductFormPayload(form) {
    const data = new FormData(form);
    const link = form.querySelector('[data-ns-product-link]');
    const publicVariant = link ? new URL(link.href, window.location.origin).searchParams.get('variant') : null;
    const variantId = Number(publicVariant || data.get('id'));
    const quantitySelect = form.querySelector('[data-ns-quantity-select]');
    const quantity = Number(quantitySelect?.value || data.get('quantity') || 1);
    if (!Number.isSafeInteger(variantId) || variantId <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) return null;
    const item = { id: variantId, quantity };
    const properties = {};
    for (const [name, value] of data.entries()) {
      if (value instanceof File && value.size) return null;
      const property = name.match(/^properties\[(.*)\]$/);
      if (property && typeof value === 'string') properties[property[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
    return { items: [item] };
  }

  onExternalAddClick(event) {
    const button = event.target.closest('product-form button[type="submit"][name="add"]');
    if (!button || !button.form || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    const payload = this.getProductFormPayload(button.form);
    if (!payload) return;
    event.preventDefault();
    event.stopPropagation();
    this.addFromProductForm(button.form, button, payload);
  }

  onExternalSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-form')) return;
    if (event.submitter?.matches('[data-ns-direct-checkout]') && form.closest('product-info.ns-classic-buybox')) return;
    const payload = this.getProductFormPayload(form);
    if (!payload) return;
    event.preventDefault();
    event.stopPropagation();
    this.addFromProductForm(form, event.submitter || form.querySelector('[name="add"]'), payload);
  }

  async addFromProductForm(form, button, payload) {
    if (this.busy || !button) return;
    const packDialog = form.closest('[data-ns-buy-dialog]');
    const trigger = packDialog?.closest('[data-ns-card]')?.querySelector('[data-ns-buy-open]') || button;
    this.setActiveElement(trigger);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      await this.mutate(this.dataset.addUrl, payload, this.strings.added);
      const error = this.querySelector('[data-ns-error]')?.textContent;
      if (packDialog && error) {
        form.closest('product-form')?.handleErrorMessage(error);
        return;
      }
      packDialog?.close();
      await this.open(trigger);
      if (error) this.showError(error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async open(triggeredBy) {
    if (this.isPage) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (!this.activeElement) this.setActiveElement(document.activeElement);
    if (this.classList.contains('active')) return;
    document.querySelectorAll('ns-header dialog[open]').forEach(dialog => dialog.close());
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    this.background = [...document.body.children].filter(node => !node.contains(this) && !['SCRIPT','LINK','STYLE','TEMPLATE'].includes(node.tagName)).map(node => ({node,inert:node.inert}));
    this.background.forEach(({node}) => { node.inert = true; });
    this.setHeaderCartIconAccessibility();
    requestAnimationFrame(() => {
      this.fitRecommendations();
      this.querySelector('[data-ns-focus="close"]')?.focus({ preventScroll: true });
    });
    if (!this.busy && Date.now() - (this.lastSyncedAt || 0) > 1500) {
      const cart = await this.readCart().catch(() => null);
      if (cart) await this.normalizeProtection(cart).catch(error => this.showError(error.message));
      if (window.NSClubAPI?.state.logged && window.NSClubAPI.state.balanceReady !== false) await window.NSCartRewards.syncClub().catch(error => this.showError(error.message));
      await this.refresh({ skipCartRead: true }).catch(error => { console.error('NasoStrips cart refresh:', error); this.showError(this.strings.error); });
    }
  }

  close() {
    this.clearUndo();
    if (this.isPage) { window.location.assign(window.Shopify?.routes?.root || '/'); return; }
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    this.background?.forEach(({node,inert}) => { node.inert = inert; });
    this.background = null;
    this.setHeaderCartIconAccessibility();
    if (this.activeElement?.isConnected) this.activeElement.focus({ preventScroll: true });
    else document.querySelector('#cart-icon-bubble')?.focus({ preventScroll: true });
  }

  onKeyDown(event) {
    if (event.key === 'Enter' && event.target.matches('[data-ns-quantity-input]')) {
      event.preventDefault();
      this.onChange(event);
      return;
    }
    if (event.key === 'Escape') {
      const opened = this.querySelector('details[open]');
      if (opened) { event.preventDefault(); opened.open = false; opened.querySelector('summary')?.focus(); return; }
      if (this.isPage) return;
      event.preventDefault();
      this.close();
      return;
    }
    if (this.isPage || event.key !== 'Tab' || !this.classList.contains('active')) return;
    const focusable = Array.from(this.panel.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), summary, [tabindex="0"]')).filter(el => el.getClientRects().length && el.getAttribute('tabindex') !== '-1');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !this.panel.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
  }

  clearUndo() {
    this.lastRemoved = null;
    const message = this.querySelector('[data-ns-undo-message]');
    if (message) message.hidden = true;
  }

  onClick(event) {
    if (event.target.closest('[data-ns-dismiss-undo]')) {
      event.preventDefault();
      this.clearUndo();
      this.querySelector('[data-ns-focus="close"]')?.focus({ preventScroll: true });
      return;
    }
    const promoRemove = event.target.closest('[data-ns-promo-remove]');
    if (promoRemove) {
      event.preventDefault();
      this.mutate(`${this.dataset.cartUrl}/update`, {}, '', { remove: promoRemove.dataset.nsPromoRemove });
      return;
    }
    const undo = event.target.closest('[data-ns-undo]');
    if (undo && this.lastRemoved) {
      event.preventDefault();
      const items = this.lastRemoved.map(item => ({id:item.variant_id,quantity:item.quantity,properties:!Array.isArray(item.properties) && item.properties || {},...(item.selling_plan ? {selling_plan:item.selling_plan} : {})}));
      this.mutate(this.dataset.addUrl, {items}, this.strings.added).then(success => { if (!success) return; this.lastRemoved = null; const message = this.querySelector('[data-ns-undo-message]'); if (message) message.hidden = true; });
      return;
    }
    const close = event.target.closest('[data-ns-close]');
    if (close) { event.preventDefault(); this.close(); return; }
    const scroll = event.target.closest('[data-ns-scroll]');
    if (scroll) {
      const grid = this.querySelector('[data-ns-reco-grid]');
      grid?.scrollBy({ left: Number(scroll.dataset.nsScroll) * grid.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      return;
    }
    const add = event.target.closest('[data-ns-add]');
    if (add) {
      event.preventDefault();
      this.mutate(this.dataset.addUrl, { items: [{ id: Number(add.dataset.nsAdd), quantity: Number(add.dataset.quantity || 1) }] }, this.strings.added);
      return;
    }
    const line = event.target.closest('[data-ns-line]');
    if (!line) return;
    const remove = event.target.closest('[data-ns-remove]');
    if (remove) { event.preventDefault(); this.changeQuantity(line, 0); return; }
    const quantityButton = event.target.closest('[data-ns-quantity]');
    if (quantityButton) {
      event.preventDefault();
      const input = line.querySelector('[data-ns-quantity-input]');
      let next = Number(input.value) + Number(quantityButton.dataset.nsQuantity) * Number(input.step || 1);
      if (next > 0 && next < Number(input.dataset.minimum || 1)) next = quantityButton.dataset.nsQuantity === '-1' ? 0 : Number(input.dataset.minimum || 1);
      this.changeQuantity(line, Math.max(0, next));
    }
  }

  onChange(event) {
    const protection = event.target.closest('[data-ns-protect-toggle]');
    if (protection) { this.changeProtection(protection); return; }
    const input = event.target.closest('[data-ns-quantity-input]');
    if (!input) return;
    const quantity = Number(input.value);
    if (!Number.isInteger(quantity) || quantity < 0 || (quantity > 0 && (!input.checkValidity() || quantity < Number(input.dataset.minimum || 1)))) {
      input.value = input.defaultValue;
      this.showError(this.strings.stock);
      return;
    }
    this.changeQuantity(input.closest('[data-ns-line]'), quantity);
  }

  changeQuantity(line, quantity) {
    const input = line.querySelector('[data-ns-quantity-input]');
    if (input.max && quantity > Number(input.max)) { input.value = input.defaultValue; this.showError(this.strings.stock); return; }
    const before = this.cartSnapshot();
    const removed = quantity === 0 ? before?.items.find(item => item.key === line.dataset.key) : null;
    const restore = removed ? [removed, ...before.items.filter(item => item.sku === 'NS-ENVIO-PROTEGIDO' && before.items.filter(row => row.sku !== 'NS-ENVIO-PROTEGIDO' && row.sku !== 'NS-REGALO-CLUB').length === 1)] : null;
    this.mutate(this.dataset.changeUrl, { id: line.dataset.key, quantity }, this.strings.updated).then(success => {
      if (success && restore && !this.cartSnapshot()?.items.some(item => item.key === removed.key)) { this.lastRemoved = restore; const message = this.querySelector('[data-ns-undo-message]'); if (message) message.hidden = false; }
    });
  }

  onPromoInput(event) {
    if (!event.target.matches('[data-ns-promo-input]')) return;
    this.promoDraft = event.target.value;
    if (this.promoFeedback.message) this.setPromoFeedback('');
  }

  onPromoSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-ns-promo-form]')) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.busy) return;
    const input = form.querySelector('[data-ns-promo-input]');
    const code = input?.value.trim();
    this.promoDraft = input?.value || '';
    if (!code || code.includes(',')) {
      this.setPromoFeedback(code ? this.strings.promo_single_code : this.strings.promo_empty, true);
      input?.focus();
      return;
    }
    this.mutate(`${this.dataset.cartUrl}/update`, {}, '', { add: code });
  }

  normalizeDiscountCodes(codes) {
    const unique = new Map();
    if (Array.isArray(codes)) codes.forEach(entry => {
      if (typeof entry?.code !== 'string' || !entry.code.trim()) return;
      const code = entry.code.trim();
      unique.set(code.toLowerCase(), { code, applicable: entry.applicable === true });
    });
    return [...unique.values()];
  }

  acceptDiscountCodes(cart) {
    if (Array.isArray(cart?.discount_codes)) this.discountCodes = this.normalizeDiscountCodes(cart.discount_codes);
  }

  setPromoFeedback(message, error = false) {
    this.promoFeedback = { message: message || '', error };
    this.paintPromo();
  }

  paintPromo() {
    const input = this.querySelector('[data-ns-promo-input]');
    if (!input) return;
    if (input.value !== this.promoDraft) input.value = this.promoDraft;
    input.setAttribute('aria-invalid', String(this.promoFeedback.error));
    const error = this.querySelector('[data-ns-promo-error]');
    const status = this.querySelector('[data-ns-promo-status]');
    if (error) {
      error.textContent = this.promoFeedback.error ? this.promoFeedback.message : '';
      error.hidden = !error.textContent;
    }
    if (status) {
      status.textContent = this.promoFeedback.error ? '' : this.promoFeedback.message;
      status.hidden = !status.textContent;
    }
    const list = this.querySelector('[data-ns-promo-codes]');
    if (!list) return;
    list.replaceChildren(...(this.discountCodes || []).map(({ code, applicable }) => {
      const item = document.createElement('li');
      item.className = `ns-cart-promo-chip${applicable ? '' : ' is-inapplicable'}`;
      const label = document.createElement('span');
      label.className = 'ns-cart-promo-chip-label';
      label.textContent = code;
      item.append(label);
      if (!applicable) {
        const state = document.createElement('small');
        state.textContent = this.strings.promo_unavailable || '';
        item.append(state);
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.nsPromoRemove = code;
      button.dataset.nsFocus = `promo-remove-${code}`;
      button.disabled = this.busy;
      button.setAttribute('aria-label', (this.strings.promo_remove || '').replace('[code]', code));
      const icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '×';
      button.append(icon);
      item.append(button);
      return item;
    }));
    list.hidden = !list.children.length;
  }

  async mutate(url, body, status, promoChange = null) {
    if (this.busy) return;
    const focusKey = promoChange ? 'promo-input' : document.activeElement?.dataset.nsFocus;
    this.setBusy(true);
    this.showError('');
    this.setPromoFeedback(promoChange ? (promoChange.add ? this.strings.promo_pending : this.strings.promo_removing) : '');
    let requestSucceeded = false;
    let mutationSent = false;
    let responseRejected = false;
    try {
      if (promoChange) {
        const current = await this.readCart();
        // The Ajax list also contains shipping and currently inapplicable codes omitted by Liquid.
        if (!Array.isArray(current.discount_codes)) throw new Error(this.strings.promo_error);
        const codes = this.normalizeDiscountCodes(current.discount_codes).map(entry => entry.code);
        if (promoChange.add) {
          if (codes.some(code => code.toLowerCase() === promoChange.add.toLowerCase())) {
            this.setPromoFeedback(this.strings.promo_duplicate, true);
            return false;
          }
          codes.push(promoChange.add);
        } else {
          const index = codes.findIndex(code => code.toLowerCase() === promoChange.remove?.toLowerCase());
          if (index === -1) {
            await this.refresh();
            this.promoDraft = '';
            this.setPromoFeedback((this.strings.promo_removed || '').replace('[code]', promoChange.remove || ''));
            return true;
          }
          codes.splice(index, 1);
        }
        body = { discount: codes.join(',') };
      }
      mutationSent = true;
      const response = await fetch(`${url}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ ...body, sections: this.getSectionsToRender().map(section => section.id), sections_url: window.location.pathname }) });
      const data = await response.json();
      if (!response.ok || data.status) { responseRejected = true; throw new Error(data.description || data.message || this.strings.error); }
      requestSucceeded = true;
      this.acceptDiscountCodes(data);
      const cart = this.cartFromSections(data) || await this.readCart();
      const cleaned = await this.normalizeProtection(cart);
      let clubChanged = false;
      const clubQueried = window.NSClubAPI?.state.logged && window.NSClubAPI.state.balanceReady !== false;
      if (clubQueried) clubChanged = await window.NSCartRewards.syncClub();
      if (cleaned.changed || clubChanged || clubQueried || !this.applySections(data)) await this.refresh();
      if (promoChange) {
        const finalCart = await this.readCart();
        if (!Array.isArray(finalCart.discount_codes)) throw new Error(this.strings.promo_uncertain);
        const changedCode = promoChange.add || promoChange.remove;
        const actual = this.discountCodes.find(entry => entry.code.toLowerCase() === changedCode.toLowerCase());
        if (promoChange.add && (!actual || !actual.applicable)) {
          this.setPromoFeedback(this.strings.promo_invalid, true);
        } else if (promoChange.remove && actual) {
          this.setPromoFeedback(this.strings.promo_error, true);
        } else {
          this.promoDraft = '';
          this.setPromoFeedback((promoChange.add ? this.strings.promo_applied : this.strings.promo_removed).replace('[code]', changedCode));
        }
      }
      this.announce(status);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: this.eventSource, cartData: data })).catch(() => {});
      return true;
    } catch (error) {
      try { if (promoChange) await this.readCart(); await this.refresh(); } catch {}
      if (promoChange) this.setPromoFeedback(mutationSent && !responseRejected ? this.strings.promo_uncertain : this.strings.promo_error, true);
      else this.showError(requestSucceeded ? this.strings.uncertain : (error.message || this.strings.error));
      return false;
    } finally {
      this.setBusy(false);
      this.restoreFocus(focusKey);
    }
  }

  setBusy(busy) {
    this.busy = busy;
    this.classList.toggle('is-busy', busy);
    this.panel?.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('[data-ns-add], [data-ns-quantity], [data-ns-remove], [data-ns-quantity-input], [data-ns-checkout], [data-ns-protect-toggle], [data-ns-undo], [data-ns-promo-input], [data-ns-promo-apply], [data-ns-promo-remove]').forEach(el => { el.disabled = busy; });
  }

  async refresh({ skipCartRead = false } = {}) {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = (async () => {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', this.getSectionsToRender().map(section => section.id).join(','));
      const [response] = await Promise.all([
        fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' }),
        skipCartRead ? Promise.resolve() : this.readCart().catch(() => null),
      ]);
      if (!response.ok) throw new Error(this.strings.error);
      const sections = await response.json();
      // Keep shipping and inactive codes from the Ajax cart without serializing both reads.
      if (!this.applySections({ sections })) throw new Error(this.strings.error);
    })();
    try { await this.refreshPromise; } finally { this.refreshPromise = null; }
  }

  applySections(state) {
    const html = state.sections?.[this.isPage ? this.dataset.sectionId : 'cart-drawer'];
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(this.isPage ? '#NsCartPage' : '#CartDrawer');
    if (!next) return false;
    const scroll = this.querySelector('.ns-cart-main')?.scrollTop || 0;
    const progress = this.querySelector('ns-cart-benefits')?.model?.ratio;
    const openedDetails = [...this.querySelectorAll('details[open]')].map(d => d.className);
    const recoScroll = this.querySelector('[data-ns-reco-grid]')?.scrollLeft || 0;
    const focusKey = document.activeElement?.dataset.nsFocus;
    this.root.replaceChildren(...Array.from(next.childNodes));
    this.root.dataset.itemCount = next.dataset.itemCount;
    this.root.dataset.lineCount = next.dataset.lineCount;
    const icon = document.querySelector('#cart-icon-bubble');
    const iconHTML = state.sections?.['cart-icon-bubble'];
    if (icon && iconHTML) {
      const iconDoc = new DOMParser().parseFromString(iconHTML, 'text/html');
      const fragment = iconDoc.querySelector('.shopify-section');
      if (fragment) icon.innerHTML = fragment.innerHTML;
    }
    this.hydrate();
    this.lastSyncedAt = Date.now();
    this.setHeaderCartIconAccessibility();
    if (this.busy) this.setBusy(true);
    const items = this.querySelector('.ns-cart-main');
    if (items) items.scrollTop = scroll;
    const grid = this.querySelector('[data-ns-reco-grid]');
    if (grid) grid.scrollLeft = recoScroll;
    this.querySelector('ns-cart-benefits')?.animateFrom(progress);
    this.querySelectorAll('details').forEach(d => { if (openedDetails.includes(d.className)) d.open = true; });
    if ((this.isPage || this.classList.contains('active')) && !this.busy) this.restoreFocus(focusKey);
    return true;
  }

  renderContents(state) {
    const wasOpen = this.classList.contains('active');
    if (!this.applySections(state)) this.refresh().catch(() => this.showError(this.strings.error));
    if (!wasOpen) this.open();
  }

  getSectionsToRender() {
    return [{ id: this.isPage ? this.dataset.sectionId : 'cart-drawer', selector: this.isPage ? '#NsCartPage' : '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }

  fitRecommendations() {
    if (!this.panel) return;
    if (this.isPage) { this.dataset.density = 'spacious'; return; }
    const lines = Number(this.root?.dataset.lineCount || 0);
    const height = this.panel.getBoundingClientRect().height || window.innerHeight;
    const density = lines >= 4 || height < 600 ? 'compact' : ((lines >= 2 && height < 780) ? 'balanced' : 'spacious');
    if (this.dataset.density !== density) this.dataset.density = density;
  }

  updateCount() {
    const count = Number(this.root?.dataset.itemCount || 0);
    const icon = document.querySelector('#cart-icon-bubble');
    if (!icon) return;
    let bubble = icon.querySelector('.cart-count-bubble');
    if (count === 0) bubble?.remove();
    else {
      if (!bubble) { bubble = document.createElement('span'); bubble.className = 'ns-h-count cart-count-bubble'; icon.append(bubble); }
      bubble.textContent = count < 100 ? String(count) : '99+';
      bubble.setAttribute('aria-hidden', 'true');
    }
    const label = count === 1 ? this.strings.cart_count_one : this.strings.cart_count;
    if (label) icon.setAttribute('aria-label', label.replace('[count]', String(count)));
  }

  restoreFocus(key) {
    if (!this.isPage && !this.classList.contains('active')) return;
    const target = key ? this.querySelector(`[data-ns-focus="${CSS.escape(key)}"]`) : null;
    (target || (!this.isPage && this.querySelector('[data-ns-focus="close"]')))?.focus({ preventScroll: true });
  }

  cartSnapshot() {
    try { return JSON.parse(this.querySelector('[data-ns-cart-state]')?.textContent || 'null'); } catch { return null; }
  }

  cartFromSections(data) {
    const html = data.sections?.[this.isPage ? this.dataset.sectionId : 'cart-drawer'];
    if (!html) return null;
    try { return JSON.parse(new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ns-cart-state]').textContent); } catch { return null; }
  }

  async readCart() {
    const response = await fetch(`${this.dataset.cartUrl}.js`, {headers:{Accept:'application/json'},cache:'no-store'});
    if (!response.ok) throw Error(this.strings.error);
    const cart = await response.json();
    this.acceptDiscountCodes(cart);
    this.paintPromo();
    return cart;
  }

  async normalizeProtection(cart) {
    const protection = cart.items.filter(item => item.sku === 'NS-ENVIO-PROTEGIDO' || item.handle === 'envio-protegido');
    const hasGoods = cart.items.some(item => item.sku !== 'NS-ENVIO-PROTEGIDO' && item.sku !== 'NS-REGALO-CLUB' && item.handle !== 'envio-protegido');
    const updates = {};
    protection.forEach((item, index) => { const quantity = hasGoods && index === 0 ? 1 : 0; if (item.quantity !== quantity) updates[item.key] = quantity; });
    if (!Object.keys(updates).length) return {cart,changed:false};
    const response = await fetch(`${this.dataset.cartUrl}/update.js`, {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({updates})});
    const next = await response.json();
    if (!response.ok || next.status) throw Error(next.description || this.strings.error);
    return {cart:next,changed:true};
  }

  changeProtection(input) {
    const cart = this.cartSnapshot();
    if (!cart) { input.checked = !input.checked; this.showError(this.strings.error); return; }
    const line = cart.items.find(item => item.sku === 'NS-ENVIO-PROTEGIDO' || item.handle === 'envio-protegido');
    if (input.checked && !line) this.mutate(this.dataset.addUrl, {items:[{id:Number(input.dataset.variantId),quantity:1}]}, this.strings.updated);
    else if (!input.checked && line) this.mutate(this.dataset.changeUrl, {id:line.key,quantity:0}, this.strings.updated);
  }

  async onClubReady() {
    this.querySelector('ns-cart-benefits')?.paint();
    if (!this.isPage || this.prepared || !window.NSClubAPI) return;
    this.prepared = true;
    this.setBusy(true);
    try {
      const cart = this.cartSnapshot() || await this.readCart();
      const cleaned = await this.normalizeProtection(cart);
      let changed = false;
      if (window.NSClubAPI.state.logged && window.NSClubAPI.state.balanceReady !== false) changed = await window.NSCartRewards.syncClub();
      if (changed || cleaned.changed) { await this.refresh(); if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {source:this.eventSource})).catch(() => {}); }
    } catch { this.showError(this.strings.error); } finally { this.setBusy(false); }
  }

  showError(message) {
    const el = this.querySelector('[data-ns-error]');
    if (el) { el.textContent = message || ''; el.hidden = !message; }
  }

  announce(message) {
    const el = this.querySelector('[data-ns-status]');
    if (el) el.textContent = message || '';
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

if (!customElements.get('ns-cart-page')) customElements.define('ns-cart-page', class extends CartDrawer {});
